var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var _TempoGridCursor_instances, _TempoGridCursor_events, _TempoGridCursor_storageBpm, _TempoGridCursor_segmentIndex, _TempoGridCursor_lastPPQN, _TempoGridCursor_lastBpm, _TempoGridCursor_m, _TempoGridCursor_q, _TempoGridCursor_reset, _TempoGridCursor_bpmAt, _TempoGridCursor_zoneAt, _VaryingTempoMap_instances, _VaryingTempoMap_terminator, _VaryingTempoMap_adapter, _VaryingTempoMap_ppqnCache, _VaryingTempoMap_secondsCache, _VaryingTempoMap_cursor, _VaryingTempoMap_ivFrom, _VaryingTempoMap_ivTo, _VaryingTempoMap_ivSeconds, _VaryingTempoMap_rebuildCache, _VaryingTempoMap_ppqnToSecondsPositive, _VaryingTempoMap_absoluteSecondsToPPQN;
import { PPQN, TempoChangeGrid } from "@opendaw/lib-dsp";
import { BinarySearch, Curve, NumberComparator, quantizeCeil, quantizeFloor, Terminator } from "@opendaw/lib-std";
/**
 * Allocation-free cursor that walks the TempoChangeGrid as a step function: tempo is constant within
 * each grid cell and sampled at the cell's grid-aligned start (matching BlockRenderer). Tracks the
 * current segment and the recurrence coefficients m, q so a step of exactly TempoChangeGrid within the
 * same zone resolves as v = m * v + q. Because every cell is sampled at its grid origin, the walk is
 * additive: integrate(a, c) === integrate(a, b) + integrate(b, c) for any b.
 */
export class TempoGridCursor {
    constructor() {
        _TempoGridCursor_instances.add(this);
        _TempoGridCursor_events.set(this, []);
        _TempoGridCursor_storageBpm.set(this, 0.0);
        _TempoGridCursor_segmentIndex.set(this, -2);
        _TempoGridCursor_lastPPQN.set(this, Number.NEGATIVE_INFINITY);
        _TempoGridCursor_lastBpm.set(this, 0.0);
        _TempoGridCursor_m.set(this, 1.0);
        _TempoGridCursor_q.set(this, 0.0
        /**
         * Integrates the seconds elapsed across [fromPPQN, toPPQN] by stepping the TempoChangeGrid.
         */
        );
    }
    /**
     * Integrates the seconds elapsed across [fromPPQN, toPPQN] by stepping the TempoChangeGrid.
     */
    integrate(events, fromPPQN, toPPQN, storageBpm) {
        __classPrivateFieldGet(this, _TempoGridCursor_instances, "m", _TempoGridCursor_reset).call(this, events, storageBpm);
        let acc = 0.0;
        let current = fromPPQN;
        while (current < toPPQN) {
            const currentBpm = __classPrivateFieldGet(this, _TempoGridCursor_instances, "m", _TempoGridCursor_bpmAt).call(this, quantizeFloor(current, TempoChangeGrid));
            const nextGrid = quantizeCeil(current, TempoChangeGrid);
            const segmentEnd = nextGrid <= current ? nextGrid + TempoChangeGrid : nextGrid;
            const actualEnd = Math.min(segmentEnd, toPPQN);
            acc += PPQN.pulsesToSeconds(actualEnd - current, currentBpm);
            current = actualEnd;
        }
        return acc;
    }
    /**
     * Walks the TempoChangeGrid forward from fromPPQN, accumulating seconds starting at fromSeconds,
     * until targetSeconds is reached.
     */
    advance(events, fromPPQN, fromSeconds, targetSeconds, storageBpm) {
        __classPrivateFieldGet(this, _TempoGridCursor_instances, "m", _TempoGridCursor_reset).call(this, events, storageBpm);
        let accumulatedSeconds = fromSeconds;
        let accumulatedPPQN = fromPPQN;
        while (accumulatedSeconds < targetSeconds) {
            const currentBpm = __classPrivateFieldGet(this, _TempoGridCursor_instances, "m", _TempoGridCursor_bpmAt).call(this, quantizeFloor(accumulatedPPQN, TempoChangeGrid));
            const nextGrid = quantizeCeil(accumulatedPPQN, TempoChangeGrid);
            const segmentEnd = nextGrid <= accumulatedPPQN ? nextGrid + TempoChangeGrid : nextGrid;
            const segmentPPQN = segmentEnd - accumulatedPPQN;
            const segmentSeconds = PPQN.pulsesToSeconds(segmentPPQN, currentBpm);
            if (accumulatedSeconds + segmentSeconds >= targetSeconds) {
                accumulatedPPQN += PPQN.secondsToPulses(targetSeconds - accumulatedSeconds, currentBpm);
                break;
            }
            accumulatedSeconds += segmentSeconds;
            accumulatedPPQN = segmentEnd;
        }
        return accumulatedPPQN;
    }
}
_TempoGridCursor_events = new WeakMap(), _TempoGridCursor_storageBpm = new WeakMap(), _TempoGridCursor_segmentIndex = new WeakMap(), _TempoGridCursor_lastPPQN = new WeakMap(), _TempoGridCursor_lastBpm = new WeakMap(), _TempoGridCursor_m = new WeakMap(), _TempoGridCursor_q = new WeakMap(), _TempoGridCursor_instances = new WeakSet(), _TempoGridCursor_reset = function _TempoGridCursor_reset(events, storageBpm) {
    __classPrivateFieldSet(this, _TempoGridCursor_events, events, "f");
    __classPrivateFieldSet(this, _TempoGridCursor_storageBpm, storageBpm, "f");
    __classPrivateFieldSet(this, _TempoGridCursor_segmentIndex, -2, "f");
    __classPrivateFieldSet(this, _TempoGridCursor_lastPPQN, Number.NEGATIVE_INFINITY, "f");
    __classPrivateFieldSet(this, _TempoGridCursor_lastBpm, 0.0, "f");
    __classPrivateFieldSet(this, _TempoGridCursor_m, 1.0, "f");
    __classPrivateFieldSet(this, _TempoGridCursor_q, 0.0, "f");
}, _TempoGridCursor_bpmAt = function _TempoGridCursor_bpmAt(position) {
    const events = __classPrivateFieldGet(this, _TempoGridCursor_events, "f");
    if (events.length === 0) {
        return __classPrivateFieldGet(this, _TempoGridCursor_storageBpm, "f");
    }
    const zone = __classPrivateFieldGet(this, _TempoGridCursor_instances, "m", _TempoGridCursor_zoneAt).call(this, position);
    if (zone === __classPrivateFieldGet(this, _TempoGridCursor_segmentIndex, "f") && position === __classPrivateFieldGet(this, _TempoGridCursor_lastPPQN, "f") + TempoChangeGrid) {
        __classPrivateFieldSet(this, _TempoGridCursor_lastPPQN, position, "f");
        return __classPrivateFieldSet(this, _TempoGridCursor_lastBpm, __classPrivateFieldGet(this, _TempoGridCursor_m, "f") * __classPrivateFieldGet(this, _TempoGridCursor_lastBpm, "f") + __classPrivateFieldGet(this, _TempoGridCursor_q, "f"), "f");
    }
    __classPrivateFieldSet(this, _TempoGridCursor_segmentIndex, zone, "f");
    __classPrivateFieldSet(this, _TempoGridCursor_lastPPQN, position, "f");
    if (zone < 0) {
        __classPrivateFieldSet(this, _TempoGridCursor_m, 1.0, "f");
        __classPrivateFieldSet(this, _TempoGridCursor_q, 0.0, "f");
        return __classPrivateFieldSet(this, _TempoGridCursor_lastBpm, zone === -1 ? events[0].value : events[events.length - 1].value, "f");
    }
    const a = events[zone];
    const b = events[zone + 1];
    const interpolation = a.interpolation;
    if (interpolation.type === "none") {
        __classPrivateFieldSet(this, _TempoGridCursor_m, 1.0, "f");
        __classPrivateFieldSet(this, _TempoGridCursor_q, 0.0, "f");
        return __classPrivateFieldSet(this, _TempoGridCursor_lastBpm, a.value, "f");
    }
    else if (interpolation.type === "linear") {
        __classPrivateFieldSet(this, _TempoGridCursor_m, 1.0, "f");
        __classPrivateFieldSet(this, _TempoGridCursor_q, (b.value - a.value) * TempoChangeGrid / (b.position - a.position), "f");
        return __classPrivateFieldSet(this, _TempoGridCursor_lastBpm, a.value + (position - a.position) / (b.position - a.position) * (b.value - a.value), "f");
    }
    else {
        const { m, q } = Curve.coefficients({
            slope: interpolation.slope,
            steps: (b.position - a.position) / TempoChangeGrid,
            y0: a.value,
            y1: b.value
        });
        __classPrivateFieldSet(this, _TempoGridCursor_m, m, "f");
        __classPrivateFieldSet(this, _TempoGridCursor_q, q, "f");
        return __classPrivateFieldSet(this, _TempoGridCursor_lastBpm, Curve.valueAt({
            slope: interpolation.slope,
            steps: b.position - a.position,
            y0: a.value,
            y1: b.value
        }, position - a.position), "f");
    }
}, _TempoGridCursor_zoneAt = function _TempoGridCursor_zoneAt(position) {
    const events = __classPrivateFieldGet(this, _TempoGridCursor_events, "f");
    if (position < events[0].position) {
        return -1;
    }
    const last = events.length - 1;
    if (position >= events[last].position) {
        return -3;
    }
    let low = 0;
    let high = last;
    while (low < high) {
        const mid = (low + high + 1) >> 1;
        if (events[mid].position <= position) {
            low = mid;
        }
        else {
            high = mid - 1;
        }
    }
    return low;
};
/**
 * TempoMap implementation that handles varying tempo (tempo automation).
 * Steps through at TempoChangeGrid intervals to match BlockRenderer behavior.
 */
export class VaryingTempoMap {
    constructor(adapter) {
        _VaryingTempoMap_instances.add(this);
        _VaryingTempoMap_terminator.set(this, new Terminator());
        _VaryingTempoMap_adapter.set(this, void 0);
        _VaryingTempoMap_ppqnCache.set(this, []);
        _VaryingTempoMap_secondsCache.set(this, []);
        _VaryingTempoMap_cursor.set(this, new TempoGridCursor());
        _VaryingTempoMap_ivFrom.set(this, Number.NaN);
        _VaryingTempoMap_ivTo.set(this, 0.0);
        _VaryingTempoMap_ivSeconds.set(this, 0.0);
        __classPrivateFieldSet(this, _VaryingTempoMap_adapter, adapter, "f");
        __classPrivateFieldGet(this, _VaryingTempoMap_terminator, "f").ownAll(adapter.box.bpm.subscribe(() => __classPrivateFieldGet(this, _VaryingTempoMap_instances, "m", _VaryingTempoMap_rebuildCache).call(this)), adapter.catchupAndSubscribeTempoAutomation(() => __classPrivateFieldGet(this, _VaryingTempoMap_instances, "m", _VaryingTempoMap_rebuildCache).call(this)));
    }
    terminate() {
        __classPrivateFieldGet(this, _VaryingTempoMap_terminator, "f").terminate();
    }
    getTempoAt(position) {
        const storageBpm = __classPrivateFieldGet(this, _VaryingTempoMap_adapter, "f").box.bpm.getValue();
        return __classPrivateFieldGet(this, _VaryingTempoMap_adapter, "f").tempoTrackEvents.mapOr(collection => collection.valueAt(position, storageBpm), storageBpm);
    }
    ppqnToSeconds(position) {
        if (position < 0) {
            return -__classPrivateFieldGet(this, _VaryingTempoMap_instances, "m", _VaryingTempoMap_ppqnToSecondsPositive).call(this, -position);
        }
        return __classPrivateFieldGet(this, _VaryingTempoMap_instances, "m", _VaryingTempoMap_ppqnToSecondsPositive).call(this, position);
    }
    secondsToPPQN(time) {
        return __classPrivateFieldGet(this, _VaryingTempoMap_instances, "m", _VaryingTempoMap_absoluteSecondsToPPQN).call(this, time);
    }
    intervalToSeconds(fromPPQN, toPPQN) {
        if (fromPPQN < 0 || toPPQN < fromPPQN) {
            return this.ppqnToSeconds(toPPQN) - this.ppqnToSeconds(fromPPQN);
        }
        const storageBpm = __classPrivateFieldGet(this, _VaryingTempoMap_adapter, "f").box.bpm.getValue();
        const tempoEvents = __classPrivateFieldGet(this, _VaryingTempoMap_adapter, "f").tempoTrackEvents;
        if (tempoEvents.isEmpty()) {
            return PPQN.pulsesToSeconds(toPPQN - fromPPQN, storageBpm);
        }
        const collection = tempoEvents.unwrap();
        if (collection.events.isEmpty()) {
            return PPQN.pulsesToSeconds(toPPQN - fromPPQN, storageBpm);
        }
        const events = collection.events.asArray();
        if (fromPPQN === __classPrivateFieldGet(this, _VaryingTempoMap_ivFrom, "f") && toPPQN >= __classPrivateFieldGet(this, _VaryingTempoMap_ivTo, "f")) {
            __classPrivateFieldSet(this, _VaryingTempoMap_ivSeconds, __classPrivateFieldGet(this, _VaryingTempoMap_ivSeconds, "f") + __classPrivateFieldGet(this, _VaryingTempoMap_cursor, "f").integrate(events, __classPrivateFieldGet(this, _VaryingTempoMap_ivTo, "f"), toPPQN, storageBpm), "f");
            __classPrivateFieldSet(this, _VaryingTempoMap_ivTo, toPPQN, "f");
            return __classPrivateFieldGet(this, _VaryingTempoMap_ivSeconds, "f");
        }
        __classPrivateFieldSet(this, _VaryingTempoMap_ivFrom, fromPPQN, "f");
        __classPrivateFieldSet(this, _VaryingTempoMap_ivTo, toPPQN, "f");
        __classPrivateFieldSet(this, _VaryingTempoMap_ivSeconds, __classPrivateFieldGet(this, _VaryingTempoMap_cursor, "f").integrate(events, fromPPQN, toPPQN, storageBpm), "f");
        return __classPrivateFieldGet(this, _VaryingTempoMap_ivSeconds, "f");
    }
    intervalToPPQN(fromSeconds, toSeconds) {
        if (fromSeconds >= toSeconds) {
            return 0.0;
        }
        const fromPPQN = __classPrivateFieldGet(this, _VaryingTempoMap_instances, "m", _VaryingTempoMap_absoluteSecondsToPPQN).call(this, fromSeconds);
        const toPPQN = __classPrivateFieldGet(this, _VaryingTempoMap_instances, "m", _VaryingTempoMap_absoluteSecondsToPPQN).call(this, toSeconds);
        return toPPQN - fromPPQN;
    }
    subscribe(observer) {
        const terminator = new Terminator();
        terminator.ownAll(__classPrivateFieldGet(this, _VaryingTempoMap_adapter, "f").box.bpm.subscribe(() => observer(this)), __classPrivateFieldGet(this, _VaryingTempoMap_adapter, "f").catchupAndSubscribeTempoAutomation(() => observer(this)));
        return terminator;
    }
}
_VaryingTempoMap_terminator = new WeakMap(), _VaryingTempoMap_adapter = new WeakMap(), _VaryingTempoMap_ppqnCache = new WeakMap(), _VaryingTempoMap_secondsCache = new WeakMap(), _VaryingTempoMap_cursor = new WeakMap(), _VaryingTempoMap_ivFrom = new WeakMap(), _VaryingTempoMap_ivTo = new WeakMap(), _VaryingTempoMap_ivSeconds = new WeakMap(), _VaryingTempoMap_instances = new WeakSet(), _VaryingTempoMap_rebuildCache = function _VaryingTempoMap_rebuildCache() {
    __classPrivateFieldGet(this, _VaryingTempoMap_ppqnCache, "f").length = 0;
    __classPrivateFieldGet(this, _VaryingTempoMap_secondsCache, "f").length = 0;
    __classPrivateFieldSet(this, _VaryingTempoMap_ivFrom, Number.NaN, "f");
    const tempoEvents = __classPrivateFieldGet(this, _VaryingTempoMap_adapter, "f").tempoTrackEvents;
    if (tempoEvents.isEmpty()) {
        return;
    }
    const collection = tempoEvents.unwrap();
    if (collection.events.isEmpty()) {
        return;
    }
    const events = collection.events.asArray();
    const storageBpm = __classPrivateFieldGet(this, _VaryingTempoMap_adapter, "f").box.bpm.getValue();
    const entries = [{ ppqn: 0, seconds: 0, bpm: collection.valueAt(0, storageBpm) }];
    let accumulatedSeconds = 0.0;
    let currentPPQN = 0.0;
    for (const event of events) {
        const eventPosition = event.position;
        if (eventPosition <= currentPPQN) {
            continue;
        }
        accumulatedSeconds += __classPrivateFieldGet(this, _VaryingTempoMap_cursor, "f").integrate(events, currentPPQN, eventPosition, storageBpm);
        currentPPQN = eventPosition;
        entries.push({ ppqn: eventPosition, seconds: accumulatedSeconds, bpm: collection.valueAt(eventPosition, storageBpm) });
    }
    __classPrivateFieldGet(this, _VaryingTempoMap_ppqnCache, "f").push(...entries);
    const sortedBySeconds = entries.slice().sort((a, b) => a.seconds - b.seconds);
    __classPrivateFieldGet(this, _VaryingTempoMap_secondsCache, "f").push(...sortedBySeconds);
}, _VaryingTempoMap_ppqnToSecondsPositive = function _VaryingTempoMap_ppqnToSecondsPositive(position) {
    if (position <= 0) {
        return 0.0;
    }
    const storageBpm = __classPrivateFieldGet(this, _VaryingTempoMap_adapter, "f").box.bpm.getValue();
    const tempoEvents = __classPrivateFieldGet(this, _VaryingTempoMap_adapter, "f").tempoTrackEvents;
    if (tempoEvents.isEmpty()) {
        return PPQN.pulsesToSeconds(position, storageBpm);
    }
    const collection = tempoEvents.unwrap();
    if (collection.events.isEmpty()) {
        return PPQN.pulsesToSeconds(position, storageBpm);
    }
    const events = collection.events.asArray();
    let startPPQN = 0.0;
    let startSeconds = 0.0;
    if (__classPrivateFieldGet(this, _VaryingTempoMap_ppqnCache, "f").length > 0) {
        const index = BinarySearch.rightMostMapped(__classPrivateFieldGet(this, _VaryingTempoMap_ppqnCache, "f"), position, NumberComparator, (entry) => entry.ppqn);
        if (index >= 0) {
            const entry = __classPrivateFieldGet(this, _VaryingTempoMap_ppqnCache, "f")[index];
            startPPQN = entry.ppqn;
            startSeconds = entry.seconds;
            if (index === __classPrivateFieldGet(this, _VaryingTempoMap_ppqnCache, "f").length - 1) {
                return startSeconds + PPQN.pulsesToSeconds(position - startPPQN, entry.bpm);
            }
        }
    }
    return startSeconds + __classPrivateFieldGet(this, _VaryingTempoMap_cursor, "f").integrate(events, startPPQN, position, storageBpm);
}, _VaryingTempoMap_absoluteSecondsToPPQN = function _VaryingTempoMap_absoluteSecondsToPPQN(targetSeconds) {
    if (targetSeconds <= 0) {
        return 0.0;
    }
    const storageBpm = __classPrivateFieldGet(this, _VaryingTempoMap_adapter, "f").box.bpm.getValue();
    const tempoEvents = __classPrivateFieldGet(this, _VaryingTempoMap_adapter, "f").tempoTrackEvents;
    if (tempoEvents.isEmpty()) {
        return PPQN.secondsToPulses(targetSeconds, storageBpm);
    }
    const collection = tempoEvents.unwrap();
    if (collection.events.isEmpty()) {
        return PPQN.secondsToPulses(targetSeconds, storageBpm);
    }
    const events = collection.events.asArray();
    let startPPQN = 0.0;
    let startSeconds = 0.0;
    if (__classPrivateFieldGet(this, _VaryingTempoMap_secondsCache, "f").length > 0) {
        const index = BinarySearch.rightMostMapped(__classPrivateFieldGet(this, _VaryingTempoMap_secondsCache, "f"), targetSeconds, NumberComparator, (entry) => entry.seconds);
        if (index >= 0) {
            const entry = __classPrivateFieldGet(this, _VaryingTempoMap_secondsCache, "f")[index];
            startPPQN = entry.ppqn;
            startSeconds = entry.seconds;
            if (index === __classPrivateFieldGet(this, _VaryingTempoMap_secondsCache, "f").length - 1) {
                return startPPQN + PPQN.secondsToPulses(targetSeconds - startSeconds, entry.bpm);
            }
        }
    }
    return __classPrivateFieldGet(this, _VaryingTempoMap_cursor, "f").advance(events, startPPQN, startSeconds, targetSeconds, storageBpm);
};
